import type {ReactElement} from 'react';
import {getAssetUrl} from '@Utils/s3.util';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {PreviewAvatar} from '@Components/social-media/components/social-post-content-multiposting/components/preview-avatar/preview-avatar';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {assertNever} from '@Utils/assert-never.util';
import type {PlatformPostPreviewProps} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {
  PreviewMediaFit,
  PreviewMediaLayout,
} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-media-wrapper/social-post-content-media-wrapper.types';
import {StoryProgressRail} from './components/story-progress-rail/story-progress-rail';
import type {StoryPreviewPlatformParts} from './story-preview.types';
import {FACEBOOK_STORY_ADD_ICON_PATH, FACEBOOK_STORY_FOOTER_ICON_PATHS, INSTAGRAM_STORY_FOOTER_ICON_PATHS, StoryPreviewPlatform} from './story-preview.types';
import styles from './story-preview.module.scss';

function getStoryPreviewPlatform(account: SocialAccount): StoryPreviewPlatform {
  switch (account.type) {
    case SocialPlatform.INSTAGRAM:
      return StoryPreviewPlatform.INSTAGRAM;
    case SocialPlatform.FACEBOOK:
    case SocialPlatform.FACEBOOK_GROUP:
    case SocialPlatform.FACEBOOK_PAGE:
    case SocialPlatform.FACEBOOK_PROFILE:
    case SocialPlatform.TWITTER:
    case SocialPlatform.TIKTOK:
    case SocialPlatform.LINKEDIN:
    case SocialPlatform.LINKEDIN_PAGE:
    case SocialPlatform.LINKEDIN_PROFILE:
    case SocialPlatform.YOUTUBE:
    case SocialPlatform.PINTEREST:
    case SocialPlatform.GOOGLE_BUSINESS_PROFILE:
    case SocialPlatform.THREADS:
      return StoryPreviewPlatform.FACEBOOK;
    default:
      return assertNever(account.type);
  }
}

function getFooterIconImages(iconPaths: string[]): ReactElement[] {
  return iconPaths.map((iconPath: string): ReactElement => <img alt="" aria-hidden="true" className={styles.storyFooterIconImage} key={iconPath} src={getAssetUrl(iconPath)} />);
}

function getFacebookStoryParts(): StoryPreviewPlatformParts {
  return {
    avatarClassName: styles.storyAvatarFacebook,
    footerActions: (
      <div className={styles.storyFooterActions}>
        {getFooterIconImages(FACEBOOK_STORY_FOOTER_ICON_PATHS)}
        <img alt="" aria-hidden="true" className={styles.storyFooterAddIcon} src={getAssetUrl(FACEBOOK_STORY_ADD_ICON_PATH)} />
      </div>
    ),
    headerActions: (
      <div className={styles.storyHeaderActions}>
        <Icon ariaHidden className={styles.storyOverlayIcon} icon="icon-menu-dots" size={IconSize.SIZE_ICON_16} />
        <Icon ariaHidden className={styles.storyOverlayIcon} icon="icon-close" size={IconSize.SIZE_ICON_16} />
      </div>
    ),
  };
}

function getInstagramStoryParts(): StoryPreviewPlatformParts {
  return {
    avatarClassName: styles.storyAvatarInstagram,
    footerActions: <div className={styles.storyFooterActions}>{getFooterIconImages(INSTAGRAM_STORY_FOOTER_ICON_PATHS)}</div>,
    headerActions: null,
  };
}

function getStoryPreviewPlatformParts(platform: StoryPreviewPlatform): StoryPreviewPlatformParts {
  switch (platform) {
    case StoryPreviewPlatform.FACEBOOK:
      return getFacebookStoryParts();
    case StoryPreviewPlatform.INSTAGRAM:
      return getInstagramStoryParts();
    default:
      return assertNever(platform);
  }
}

export function StoryPreview({account, numberOfPages, renderMedia}: PlatformPostPreviewProps): ReactElement {
  const platformParts = getStoryPreviewPlatformParts(getStoryPreviewPlatform(account));

  const getMedia = (): ReactElement => {
    return renderMedia({
      layout: PreviewMediaLayout.SINGLE,
      fit: PreviewMediaFit.COVER,
      frameClassName: styles.storyMediaFrame,
      enableVideoAutoplay: true,
    });
  };

  const getFooter = (): ReactElement => {
    return (
      <div className={styles.storyFooter}>
        <span className={styles.storyFooterSkeletonBar} aria-hidden="true" />
        {platformParts.footerActions}
      </div>
    );
  };

  const getHeader = (): ReactElement => {
    return (
      <div className={styles.storyHeader}>
        <div className={styles.storyIdentity}>
          <PreviewAvatar account={account} className={platformParts.avatarClassName} />
          <Text bold className={styles.storyAccountName} size={TextSize.XS} val={account.name} />
        </div>
        {platformParts.headerActions}
      </div>
    );
  };

  const getOverlay = (): ReactElement => {
    return (
      <div className={styles.storyOverlay}>
        <StoryProgressRail numberOfPages={numberOfPages} />
        {getHeader()}
      </div>
    );
  };

  return (
    <div className={styles.storyPreview}>
      {getMedia()}
      {getFooter()}
      {getOverlay()}
    </div>
  );
}
